import { Card, CardContent, Button } from "@mui/material";
import { Exercise, WorkoutDayExercise } from "../../../../service/model/workout.model";
import { AddIcon } from "../../../../shared/icons";
import { useEffect, useState } from "react";
import ExerciseRow from "./exercise-row";
import theme from "../../../../app/app-theme";

// export type ExerciseWithDetails = {
//     id?: number;
//     name?: string;
//     quantity?: number;
//     unit?: Unit;
// };

interface WorkoutCardProps {
    workoutDayExercises: WorkoutDayExercise[];
    exercises: Exercise[];
    onExercisesChange?: (exercises: WorkoutDayExercise[]) => void;
}

const WorkoutCard: React.FC<WorkoutCardProps> = ({ workoutDayExercises, exercises, onExercisesChange }) => {
    const [selectedExercises, setSelectedExercises] = useState<WorkoutDayExercise[]>(workoutDayExercises ?? []);

    const handleAddExercise = () => {
        const newItem: WorkoutDayExercise = {
            id: undefined,
            exercise: undefined,
            orderIndex: selectedExercises.length,
            sets: undefined,
            reps: undefined,
            targetWeight: undefined,
            targetDuration: undefined,
            restSeconds: undefined,
            notes: undefined,
        };
        setSelectedExercises((prevItems) => [...prevItems, newItem]);
    };

    useEffect(() => {
        const exerciseList: WorkoutDayExercise[] = selectedExercises.map((exercise) => ({
            id: exercise.id,
            exercise: exercises.find((ex) => ex.id === exercise.exercise?.id),
            orderIndex: exercise.orderIndex,
            sets: exercise.sets,
            reps: exercise.reps,
            targetWeight: exercise.targetWeight,
            targetDuration: exercise.targetDuration,
            restSeconds: exercise.restSeconds,
            notes: exercise.notes,
        })); // Filter out items without a valid exercise
        onExercisesChange?.(exerciseList);
    }, [selectedExercises, exercises]);

    return (
        <Card sx={{ p: 2, mb: 2, height: "100%", display: "flex", flexDirection: "column", backgroundColor: theme.palette.info.light }}>
            <CardContent>
                {selectedExercises.map((item) => (
                    <ExerciseRow
                        key={`${item.orderIndex}-${item.exercise?.id ?? 'new'}`}
                        exercise={item}
                        exercises={exercises.filter((ex) => !selectedExercises.some((se) => se.exercise?.id === ex.id) || ex.id === item.exercise?.id)} // Exclude already selected exercise items except the current one
                        onExerciseChange={(ex) => {
                            setSelectedExercises((prevItems) => {
                                const list = prevItems.map((i) =>
                                    i.exercise?.id === item.exercise?.id ? { ...i, exercise: ex } : i
                                );
                                return list;
                            });
                        }}
                        onSetsChange={(sets) => {
                            setSelectedExercises((prevItems) =>
                                prevItems.map((i) =>
                                    i.exercise?.id === item.exercise?.id ? { ...i, sets } : i
                                )
                            );
                        }}
                        onRepsChange={(reps) => {
                            setSelectedExercises((prevItems) =>
                                prevItems.map((i) =>
                                    i.exercise?.id === item.exercise?.id ? { ...i, reps } : i
                                )
                            );
                        }}
                        onTargetWeightChange={(targetWeight) => {
                            setSelectedExercises((prevItems) =>
                                prevItems.map((i) =>
                                    i.exercise?.id === item.exercise?.id ? { ...i, targetWeight } : i
                                )
                            );
                        }}
                        onRestSecondsChange={(restSeconds) => {
                            setSelectedExercises((prevItems) =>
                                prevItems.map((i) =>
                                    i.exercise?.id === item.exercise?.id ? { ...i, restSeconds } : i
                                )
                            );
                        }}
                        onTargetDurationChange={(targetDuration) => {
                            setSelectedExercises((prevItems) =>
                                prevItems.map((i) =>
                                    i.exercise?.id === item.exercise?.id ? { ...i, targetDuration } : i
                                )
                            );
                        }}
                        onDelete={() => {
                            setSelectedExercises((prevItems) =>
                                prevItems.filter((i) => i.exercise?.id !== item.exercise?.id)
                            );
                        }}
                    />
                ))}
                <Button
                    variant="outlined"
                    sx={{ mt: 3, mb: 2, justifyContent: 'center', backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText }}
                    onClick={handleAddExercise}
                >
                    <AddIcon sx={{ mr: 1 }} /> Add exercise
                </Button>
            </CardContent>
        </Card>
    );
};

export default WorkoutCard;
